import { useNotifications, Notification } from '@/hooks/useNotifications';
import { useLocale } from '@/contexts/LocaleContext';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Mail, Eye, User, Bell, CheckCheck, X, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useState } from 'react';
import { INTERACTION_STATES, FORM_CONTROLS, SPACING, TYPOGRAPHY, RESPONSIVE } from '@/design-system';
import { cn } from '@/lib/utils';
import { useLocalizations } from '@/hooks/useLocalizations';
import { LCommon } from '@/components/i18n';
import { NotificationCardSkeleton, ListSkeleton } from '@/components/skeletons';

interface NotificationCenterProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const notificationIcons = {
  message: Mail,
  opportunity: Mail,
  profile_view: Eye,
  profile_incomplete: User,
  system: Bell,
};

export function NotificationCenter({ open, onOpenChange }: NotificationCenterProps) {
  const { notifications, unreadCount, isLoading, markAsRead, markAllAsRead } = useNotifications();
  const navigate = useNavigate();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const { lCommon } = useLocalizations();
  const { formatRelativeTime } = useLocale();

  // Helper to get translated notification type label
  const getNotificationTypeLabel = (type: string): string => {
    const typeKey = `notification_types.${type}`;
    const fallbackLabel = type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    return lCommon(typeKey, { defaultValue: fallbackLabel });
  };

  // Helper to get translated text for a notification
  const getNotificationText = (notification: Notification, field: 'title' | 'message'): string => {
    const text = notification[field];
    
    // Check if it's a translation key (starts with namespace)
    if (typeof text === 'string' && text.includes('.') && !text.includes(' ')) {
      // It's a translation key - translate it with defaultValue fallback
      const vars = notification.data?.translationVars || {};
      return lCommon(text, { ...vars, defaultValue: text });
    }
    
    // It's already translated text - return as is
    return typeof text === 'string' ? text : '';
  };

  // Group notifications by type
  const groupedNotifications = notifications.reduce((acc, notification) => {
    const key = notification.type === 'profile_incomplete' ? 'profile_incomplete' : notification.id;
    
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(notification);
    return acc;
  }, {} as Record<string, Notification[]>);

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read
    if (!notification.is_read) {
      markAsRead(notification.id);
    }

    // Navigate to link if provided
    if (notification.link) {
      navigate(notification.link);
      onOpenChange(false);
    }
  };

  const handleDismissNotification = (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    markAsRead(notificationId);
    toast.success(lCommon('toast.notification_dismissed.success', { defaultValue: 'Notification dismissed successfully.' }));
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className={cn(RESPONSIVE.flexRowBetween)}>
            <div className={cn(RESPONSIVE.flexRowCenterGap2)}>
              <Bell className="h-5 w-5" />
              <LCommon id="notifications.title" defaultValue='Notifications' />
              {unreadCount > 0 && (
                <Badge variant="destructive" className="h-5 min-w-5 px-1">
                  {unreadCount}
                </Badge>
              )}
            </div>
          </SheetTitle>
          <SheetDescription>
            <LCommon
              id="notifications.description"
              defaultValue='Review your recent account activity and manage alerts for important updates.' />
          </SheetDescription>
        </SheetHeader>

        <Separator className="my-4" />

        {/* Mark all as read button */}
        {unreadCount > 0 && (
          <div className={cn(SPACING.sectionHeader)}>
            <Button
              variant="outline"
              size="sm"
              onClick={() => markAllAsRead()}
              className="w-full"
            >
              <CheckCheck className="h-4 w-4 mr-2" />
              <LCommon id="notifications.mark_all_read" defaultValue='Mark All as Read' />
            </Button>
          </div>
        )}

        {/* Notifications List */}
        <div className={cn('flex flex-col', SPACING.listItemGap, 'overflow-y-auto max-h-[calc(100vh-200px)]')}>
          {isLoading ? (
            <ListSkeleton count={5} skeleton={NotificationCardSkeleton} />
          ) : notifications.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Bell className={cn(SPACING.sectionHeader, 'h-16 w-16 text-muted-foreground')} />
                <h3 className={`${TYPOGRAPHY.sectionTitle} mb-2`}><LCommon
                  id="notifications.no_notifications.title"
                  defaultValue='No notifications available' /></h3>
                <p className={cn(TYPOGRAPHY.cardDescription, 'text-center')}>
                  <LCommon id="notifications.all_caught_up" defaultValue='No new notifications.' />
                </p>
              </CardContent>
            </Card>
          ) : (
            Object.entries(groupedNotifications).map(([groupKey, groupNotifications]) => {
              const isGroup = groupNotifications.length > 1;
              const firstNotification = groupNotifications[0];
              const unreadInGroup = groupNotifications.filter(n => !n.is_read).length;
              const Icon = notificationIcons[firstNotification.type] || Bell;
              const isExpanded = expandedGroups.has(groupKey);

              if (isGroup && firstNotification.type === 'profile_incomplete') {
                // Render grouped profile_incomplete notifications
                return (
                  <Card key={groupKey} className={unreadInGroup > 0 ? 'border-muted-foreground/30 bg-muted/50' : ''}>
                    <Collapsible open={isExpanded} onOpenChange={() => toggleGroup(groupKey)}>
                      <CardContent className={SPACING.cardContentCompact}>
                        <CollapsibleTrigger className={cn(FORM_CONTROLS.button.collapsibleTrigger, INTERACTION_STATES.collapsible.trigger, "rounded-md -m-2 p-2")}>
                          <div className="flex gap-3">
                            {/* Icon */}
                            <div
                              className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${
                                unreadInGroup > 0
                                  ? 'bg-foreground text-background'
                                  : 'bg-muted text-muted-foreground'
                              }`}
                            >
                              <Icon className="h-5 w-5" />
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0 text-left">
                              <div className="flex items-start justify-between gap-2 mb-1">
                                 <h4
                                   className={`text-sm font-semibold ${
                                     unreadInGroup > 0 ? 'text-foreground' : 'text-muted-foreground'
                                   }`}
                                 >
                                   {getNotificationText(firstNotification, 'title')}
                                 </h4>
                                <div className={cn(RESPONSIVE.flexRowCenterGap2, 'shrink-0')}>
                                  <ChevronDown
                                    className={`h-4 w-4 transition-transform ${
                                      isExpanded ? 'rotate-180' : ''
                                    }`}
                                  />
                                  {unreadInGroup > 0 && (
                                    <Badge variant="secondary" className="h-5 min-w-5 px-1 bg-foreground text-background">
                                      {unreadInGroup}
                                    </Badge>
                                  )}
                                </div>
                              </div>

                               <p className={cn(TYPOGRAPHY.cardDescription, 'mb-2')}>
                                 {lCommon('notifications.missing_fields', { count: groupNotifications.length, unread: unreadInGroup, defaultValue: 'Missing fields.' })}
                               </p>

                              <div className={cn(RESPONSIVE.flexRowCenterGap2)}>
                                <span className={cn(TYPOGRAPHY.caption)}>
                                  {formatRelativeTime(firstNotification.created_at)}
                                </span>
                                <Badge variant="outline" className="text-[10px] px-1 py-0">
                                  {getNotificationTypeLabel('profile_incomplete')}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </CollapsibleTrigger>

                        <CollapsibleContent className="mt-2 pt-2 border-t">
                          <div className={cn('flex flex-col', SPACING.listItemGap, 'pl-13')}>
                            {groupNotifications.map((notification) => (
                              <div
                                key={notification.id}
                                className={`p-3 rounded-lg cursor-pointer transition-colors hover:bg-accent ${
                                  !notification.is_read ? 'bg-muted' : ''
                                }`}
                                onClick={() => handleNotificationClick(notification)}
                              >
                                 <div className="flex items-start justify-between gap-2 mb-1">
                                   <h5 className={cn(TYPOGRAPHY.cardTitleSmall)}>{getNotificationText(notification, 'title')}</h5>
                                   <div className="flex items-center gap-1 shrink-0">
                                    {!notification.is_read && (
                                   <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0 hover:bg-muted"
                                        onClick={(e) => handleDismissNotification(e, notification.id)}
                                      >
                                        <X className="h-3.5 w-3.5" />
                                      </Button>
                                    )}
                                    {!notification.is_read && (
                                      <div className="h-2 w-2 rounded-full bg-foreground" />
                                     )}
                                   </div>
                                 </div>
                                 <p className={cn(TYPOGRAPHY.caption)}>{getNotificationText(notification, 'message')}</p>
                               </div>
                            ))}
                          </div>
                        </CollapsibleContent>
                      </CardContent>
                    </Collapsible>
                  </Card>
                );
              }

              // Render single notification
              const notification = firstNotification;
              return (
                <Card
                  key={notification.id}
                  className={`cursor-pointer transition-colors hover:bg-muted/50 ${
                    !notification.is_read ? 'border-muted-foreground/30 bg-muted/50' : ''
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <CardContent className={SPACING.cardContentCompact}>
                    <div className="flex gap-3">
                      {/* Icon */}
                      <div
                        className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${
                          !notification.is_read
                            ? 'bg-foreground text-background'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        <Icon className="h-5 w-5" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                           <h4
                             className={`text-sm font-semibold ${
                               !notification.is_read ? 'text-foreground' : 'text-muted-foreground'
                             }`}
                           >
                             {getNotificationText(notification, 'title')}
                           </h4>
                          <div className="flex items-center gap-1 shrink-0">
                            {notification.type === 'profile_incomplete' && !notification.is_read && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 hover:bg-muted"
                                onClick={(e) => handleDismissNotification(e, notification.id)}
                              >
                                <X className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {!notification.is_read && (
                              <div className="h-2 w-2 rounded-full bg-foreground" />
                            )}
                          </div>
                         </div>

                         <p className={cn(TYPOGRAPHY.cardDescription, 'mb-2')}>
                           {getNotificationText(notification, 'message')}
                         </p>

                        <div className={cn(RESPONSIVE.flexRowCenterGap2)}>
                          <span className={cn(TYPOGRAPHY.caption)}>
                            {formatRelativeTime(notification.created_at)}
                          </span>
                          
                          {/* Type badge */}
                          <Badge variant="outline" className="text-[10px] px-1 py-0">
                            {getNotificationTypeLabel(notification.type)}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
